import React from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ContactPage, Phone, Email } from "@mui/icons-material";

const SpecificPersonalInfoXs = ({ id }) => {
  const { t } = useTranslation();

  // Section component for consistent styling
  const Section = ({ title, children }) => (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="subtitle2"
        fontWeight="600"
        sx={{
          mb: 1.5,
          color: "#333",
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );

  return (
    <Box
      sx={{
        mt: 2,
        mb: 2,
        p: 1,
        maxWidth: "100%",
        overflow: "hidden",
        backgroundColor: "#f5f5f7",
      }}
      id={id}
    >
      {/* iOS-style header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          mb: 2,
          pb: 1,
          borderBottom: "1px solid rgba(0,0,0,0.1)",
        }}
      >
        <Typography
          variant="subtitle1"
          component="h1"
          fontWeight="600"
          sx={{ textAlign: "center" }}
        >
          {t("privacy-policy.specific-personal-info.title")}
        </Typography>
      </Box>

      {/* Main card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 3,
            }}
          >
            <ContactPage sx={{ color: "#007AFF", fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight="600" sx={{ ml: 1.5 }}>
              {t("privacy-policy.specific-personal-info.title")}
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: "#666",
              mb: 3,
            }}
          >
            {t("privacy-policy.specific-personal-info.intro")}
          </Typography>

          <Section
            title={t("privacy-policy.specific-personal-info.company.title")}
          >
            <Typography variant="body2" sx={{ color: "#666" }}>
              {t("privacy-policy.specific-personal-info.company.name")}
            </Typography>
          </Section>
        </CardContent>
      </Card>

      {/* Compliance card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Section
            title={t("privacy-policy.specific-personal-info.compliance.title")}
          >
            <Typography variant="body2" sx={{ color: "#666" }}>
              {t("privacy-policy.specific-personal-info.compliance.text")}
            </Typography>
          </Section>
        </CardContent>
      </Card>

      {/* Security card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Section
            title={t("privacy-policy.specific-personal-info.security.title")}
          >
            <Typography variant="body2" sx={{ color: "#666" }}>
              {t("privacy-policy.specific-personal-info.security.text")}
            </Typography>
          </Section>
        </CardContent>
      </Card>

      {/* Inquiries card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          mb: 2,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>
              {t("privacy-policy.specific-personal-info.inquiries.title")}
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              {t("privacy-policy.specific-personal-info.inquiries.department")}
            </Typography>
          </Box>

          <Divider />

          <List disablePadding>
            <ListItem sx={{ py: 1.5, px: 2 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Phone sx={{ color: "#007AFF", fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={t(
                  "privacy-policy.specific-personal-info.inquiries.phone"
                )}
                primaryTypographyProps={{
                  variant: "body2",
                  sx: { color: "#333" },
                }}
              />
            </ListItem>

            <Divider variant="inset" component="li" sx={{ ml: 2 }} />

            <ListItem sx={{ py: 1.5, px: 2 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Email sx={{ color: "#007AFF", fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={t(
                  "privacy-policy.specific-personal-info.inquiries.email"
                )}
                primaryTypographyProps={{
                  variant: "body2",
                  sx: { color: "#333" },
                }}
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Footer card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "#8E8E93",
              mb: 0.5,
            }}
          >
            {t("privacy-policy.specific-personal-info.footer.dates")}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "#8E8E93",
            }}
          >
            {t("privacy-policy.specific-personal-info.footer.company")}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SpecificPersonalInfoXs;
